import React from 'react';
import {
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Toolbar,
  IconButton,
  Typography,
  Box,
  InputBase,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Button,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search,
  Notifications,
  AccountCircle,
  MoreVert,
  Home,
  ArrowBack,
  Settings,
  ExitToApp,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';

const StyledAppBar = styled(MuiAppBar)<{ 
  variant?: 'default' | 'dense' | 'prominent'; 
  transparent?: boolean;
}>(({ theme, variant = 'default', transparent = false }) => ({
  ...(variant === 'dense' && {
    minHeight: 48,
    '& .MuiToolbar-root': {
      minHeight: 48,
      paddingTop: 0,
      paddingBottom: 0,
    },
  }),
  ...(variant === 'prominent' && {
    minHeight: 80,
    '& .MuiToolbar-root': {
      minHeight: 80,
    },
  }),
  ...(transparent && {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    boxShadow: 'none',
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  }),
}));

const SearchContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

export interface BreadcrumbItem {
  /**
   * Breadcrumb label
   */
  label: string;
  /**
   * Navigation path
   */
  href?: string;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Whether this is the current page
   */
  current?: boolean;
}

export interface UserMenuAction {
  /**
   * Unique identifier
   */
  id: string;
  /**
   * Display label
   */
  label: string;
  /**
   * Icon component
   */
  icon?: React.ReactNode;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Whether to show divider after this item
   */
  divider?: boolean;
  /**
   * Whether item is disabled
   */
  disabled?: boolean;
}

export interface AppBarProps extends Omit<MuiAppBarProps, 'variant' | 'title'> {
  /**
   * AppBar title
   */
  title?: React.ReactNode;
  /**
   * Subtitle text
   */
  subtitle?: string;
  /**
   * Visual variant
   */
  variant?: 'default' | 'dense' | 'prominent';
  /**
   * Whether to show search functionality
   */
  showSearch?: boolean;
  /**
   * Search placeholder text
   */
  searchPlaceholder?: string;
  /**
   * Search value
   */
  searchValue?: string;
  /**
   * Search change handler
   */
  onSearchChange?: (value: string) => void;
  /**
   * Whether to show menu button
   */
  showMenuButton?: boolean;
  /**
   * Menu button click handler
   */
  onMenuClick?: () => void;
  /**
   * Whether to show back button
   */
  showBackButton?: boolean;
  /**
   * Back button click handler
   */
  onBackClick?: () => void;
  /**
   * User information for profile menu
   */
  user?: {
    name: string;
    email?: string;
    avatar?: string;
  };
  /**
   * User menu actions
   */
  userMenuActions?: UserMenuAction[];
  /**
   * Notification count
   */
  notificationCount?: number;
  /**
   * Notification click handler
   */
  onNotificationClick?: () => void;
  /**
   * Breadcrumb items
   */
  breadcrumbs?: BreadcrumbItem[];
  /**
   * Custom actions/buttons
   */
  actions?: React.ReactNode[];
  /**
   * Whether AppBar should be transparent
   */
  transparent?: boolean;
  /**
   * Custom content for the left side
   */
  leftContent?: React.ReactNode;
  /**
   * Custom content for the right side
   */
  rightContent?: React.ReactNode;
}

export const AppBar: React.FC<AppBarProps> = ({
  title,
  subtitle,
  variant = 'default',
  showSearch = false,
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  showMenuButton = false,
  onMenuClick,
  showBackButton = false,
  onBackClick,
  user,
  userMenuActions = [],
  notificationCount = 0,
  onNotificationClick,
  breadcrumbs = [],
  actions = [],
  transparent = false,
  leftContent,
  rightContent,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleUserMenuAction = (action: UserMenuAction) => {
    action.onClick?.();
    handleUserMenuClose();
  };

  const renderLeftSection = () => {
    if (leftContent) return leftContent;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showBackButton && (
          <IconButton
            color="inherit"
            onClick={onBackClick}
            edge="start"
          >
            <ArrowBack />
          </IconButton>
        )}
        
        {showMenuButton && (
          <IconButton
            color="inherit"
            onClick={onMenuClick}
            edge="start"
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box>
          {breadcrumbs.length > 0 ? (
            <Breadcrumbs 
              sx={{ color: 'inherit' }}
              separator="›"
            >
              {breadcrumbs.map((item, index) => (
                <Link
                  key={index}
                  color="inherit"
                  href={item.href}
                  onClick={item.onClick}
                  sx={{
                    textDecoration: item.current ? 'none' : 'underline',
                    fontWeight: item.current ? 'bold' : 'normal',
                    cursor: item.current ? 'default' : 'pointer',
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Breadcrumbs>
          ) : (
            <>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {subtitle}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  const renderRightSection = () => {
    if (rightContent) return rightContent;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showSearch && !isMobile && (
          <SearchContainer>
            <SearchIconWrapper>
              <Search />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              inputProps={{ 'aria-label': 'search' }}
            />
          </SearchContainer>
        )}

        {actions.map((action, index) => (
          <Box key={index}>{action}</Box>
        ))}

        {onNotificationClick && (
          <IconButton color="inherit" onClick={onNotificationClick}>
            <Badge badgeContent={notificationCount} color="error">
              <Notifications />
            </Badge>
          </IconButton>
        )}

        {user && (
          <>
            <IconButton
              color="inherit"
              onClick={handleUserMenuOpen}
              sx={{ p: 0 }}
            >
              {user.avatar ? (
                <Avatar src={user.avatar} sx={{ width: 32, height: 32 }} />
              ) : (
                <Avatar sx={{ width: 32, height: 32 }}>
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              )}
            </IconButton>
            
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              onClick={handleUserMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="subtitle2">{user.name}</Typography>
                  {user.email && (
                    <Typography variant="body2" color="textSecondary">
                      {user.email}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              
              {userMenuActions.map((action) => (
                <Box key={action.id}>
                  <MenuItem
                    onClick={() => handleUserMenuAction(action)}
                    disabled={action.disabled}
                  >
                    {action.icon && (
                      <Box sx={{ mr: 2, display: 'flex' }}>
                        {action.icon}
                      </Box>
                    )}
                    {action.label}
                  </MenuItem>
                  {action.divider && <hr style={{ margin: 0, border: 'none', borderTop: '1px solid #e0e0e0' }} />}
                </Box>
              ))}
            </Menu>
          </>
        )}

        {showSearch && isMobile && (
          <IconButton color="inherit">
            <Search />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <StyledAppBar
      transparent={transparent}
      {...props}
    >
      <Toolbar>
        {renderLeftSection()}
        <Box sx={{ flexGrow: 1 }} />
        {renderRightSection()}
      </Toolbar>
    </StyledAppBar>
  );
}; 